import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom';
import { getUsersCourses, removeAUserFromACourse } from '../../../../../Constants/Api/functions/function';
import { Button, List, Popconfirm, Spin, notification } from 'antd';
import {
    PlusOutlined,
    ExportOutlined
} from '@ant-design/icons';

import './styles.scss'
import { DEFAULT_COURSE_COVER_IMAGE } from '../../../../../Constants/Api/azure/azure';
import AddUserCourse from './AddUserCourse';
import { useSelector } from 'react-redux';

const InstallmentCountdown = ({ dueDateStr }) => {
    const [timeLeft, setTimeLeft] = useState('');
    const [isOverdue, setIsOverdue] = useState(false);
    
    useEffect(() => {
        if (!dueDateStr) return;
        
        const calculateTime = () => {
            const dueDate = new Date(dueDateStr);
            const now = new Date();
            const difference = +dueDate - +now;
            
            if (difference < 0) {
                setIsOverdue(true);
                const absDiff = Math.abs(difference);
                const days = Math.floor(absDiff / (1000 * 60 * 60 * 24));
                return `Overdue by ${days} day(s)`;
            }
            
            setIsOverdue(false);
            const days = Math.floor(difference / (1000 * 60 * 60 * 24));
            const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
            return `Next payment due in ${days}d ${hours}h`;
        };

        setTimeLeft(calculateTime());
        const timer = setInterval(() => {
            setTimeLeft(calculateTime());
        }, 60000);

        return () => clearInterval(timer);
    }, [dueDateStr]);

    if (!timeLeft) return null;

    return (
        <div style={{
            padding: '12px',
            background: isOverdue ? '#fff1f0' : '#e6f7ff',
            border: `1px solid ${isOverdue ? '#ffa39e' : '#91d5ff'}`,
            borderRadius: '8px',
            marginBottom: '16px',
            color: isOverdue ? '#cf1322' : '#096dd9',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        }}>
            <span style={{ fontSize: '18px' }}>{isOverdue ? '⚠️' : '⏱'}</span>
            {timeLeft}
        </div>
    );
};

const UserCources = () => {
    const [cources, setCources] = useState([])
    const userId = useParams().id
    const [isAddUserCourseOpen, setIsAddUserCourseOpen] = useState(false)
    const userProfile = useSelector(state => state.userProfile.userProfile);
    const [isLoading, setIsLoading] = useState(false)
    const [api, contextHolder] = notification.useNotification();

    useEffect(() => {
        if (userId) {
            handleGetUserCources()
        }
    }, [userId])

    const handleGetUserCources = async () => {
        setIsLoading(true)
        try {
            const userCources = await getUsersCourses(userId)
            setCources(userCources)
        } catch (error) {
            console.error('handleGetUserCources', error)
        }
        setIsLoading(false)
    }

    const handleRemoveCourse = async (courseId) => {
        setIsLoading(true)
        try {
            const response = await removeAUserFromACourse(userId, courseId);
            if (response) {
                handleGetUserCources();
                api.success({
                    message: 'Course removed successfully'
                })
            }
        } catch (error) {
            console.error('handleDeleteUser', error);
            api.error({
                message: 'Failed to remove course'
            })
        }
        setIsLoading(false)
    }

    const renderFinancialInfo = (course) => {
        if (userProfile?.role !== 'student') return null;
        
        let totalPaid = 0;
        let outstandingBalance = 0;
        let nextPaymentDate = null;
        let nextPaymentAmount = 0;

        const isPromoActiveAtEnrollment = () => {
            if (!course?.isPromoActive || !course?.promoPrice) return false;
            if (course.promoStartDate && course.promoEndDate && course.enrolledAt) {
                const enrolled = new Date(course.enrolledAt);
                const start = new Date(course.promoStartDate);
                const end = new Date(course.promoEndDate);
                end.setHours(23, 59, 59, 999);
                return enrolled >= start && enrolled <= end;
            }
            return false;
        };

        const activePromo = isPromoActiveAtEnrollment();
        const fullPrice = activePromo ? course.promoPrice : course.price;
        const installments = course.installmentBreakdown || [];

        if (course.isPaid) {
            totalPaid = installments.length > 0 ? installments.reduce((acc, inst) => acc + parseFloat(inst.amount), 0) : parseFloat(fullPrice);
            outstandingBalance = 0;
        } else {
            const paidCount = course.payedInstallments || 0;
            if (installments.length === 0) {
                // Not paid, and no installments => full amount outstanding
                outstandingBalance = parseFloat(fullPrice);
                nextPaymentAmount = outstandingBalance;
                nextPaymentDate = 'Immediate';
            } else {
                installments.forEach((inst, idx) => {
                    if (idx < paidCount) {
                        totalPaid += parseFloat(inst.amount);
                    } else {
                        outstandingBalance += parseFloat(inst.amount);
                        
                        if (!nextPaymentDate && idx === paidCount) {
                            nextPaymentAmount = parseFloat(inst.amount);
                            if (inst.dueDate) {
                                nextPaymentDate = new Date(inst.dueDate).toLocaleDateString();
                                // Store raw date for countdown
                                course.nextDueDateRaw = inst.dueDate;
                            } else {
                                nextPaymentDate = 'Immediate';
                            }
                        }
                    }
                });
            }
        }

        return (
            <div style={{ marginTop: '16px', padding: '12px', background: '#f9f9f9', borderRadius: '8px', border: '1px solid #e8e8e8' }}>
                {!course.isPaid && course.nextDueDateRaw && (
                    <InstallmentCountdown dueDateStr={course.nextDueDateRaw} />
                )}
                <h4 style={{ marginBottom: '8px', color: '#1890ff' }}>Payment Summary</h4>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                    <div><strong>Total Paid:</strong> {totalPaid} LKR</div>
                    <div><strong>Outstanding Balance:</strong> {outstandingBalance} LKR</div>
                    {!course.isPaid && nextPaymentDate && (
                        <div><strong>Next Payment:</strong> <span style={{color: '#faad14', fontWeight: 'bold'}}>{nextPaymentAmount} LKR</span> due on <span style={{fontWeight: 'bold'}}>{nextPaymentDate}</span></div>
                    )}
                    {course.isPaid && (
                        <div style={{ color: 'green', fontWeight: 'bold' }}>Fully Paid</div>
                    )}
                </div>
                {installments.length > 0 && !course.isPaid && (
                    <div style={{ marginTop: '10px' }}>
                        <strong>Installment Schedule:</strong>
                        <ul style={{ paddingLeft: '20px', marginTop: '4px', marginBottom: 0 }}>
                            {installments.map((inst, idx) => {
                                const isPaid = idx < (course.payedInstallments || 0);
                                let dueText = 'At registration';
                                if (inst.dueDate) {
                                    dueText = new Date(inst.dueDate).toLocaleDateString();
                                }
                                return (
                                    <li key={idx} style={{ color: isPaid ? 'green' : 'black', textDecoration: isPaid ? 'line-through' : 'none' }}>
                                        Installment {inst.installmentNumber}: {inst.amount} LKR {isPaid ? '(Paid)' : `(Due: ${dueText})`}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className='user-cources'>
            {contextHolder}
            <Spin
                spinning={isLoading}
                tip="Please wait..."
            >
                <div className='header'>
                    <h1>
                        User's Cources
                    </h1>
                    {userProfile?.role === 'superadmin' && <Button
                        type='primary'
                        shape='round'
                        style={{ width: '140px' }}
                        onClick={() => setIsAddUserCourseOpen(true)}
                    >
                        <PlusOutlined />
                        Add Course
                    </Button>
                    }

                    {userProfile?.role === 'student' &&
                        <Link to='/dashboard/cources'>
                            <Button
                                type='primary'
                                shape='round'
                                style={{ width: '140px' }}
                            >
                                <PlusOutlined />
                                Buy A Course
                            </Button>
                        </Link>
                    }
                </div>

                <div className='cources'>
                    <List
                        itemLayout='vertical'
                        size='large'
                        dataSource={cources}
                        renderItem={item => (
                            <List.Item
                                className='cource-item'
                                key={item.id}
                                actions={[
                                    <>
                                        <Link to={`/dashboard/cources/${item.id}`}>
                                            View <ExportOutlined />
                                        </Link>

                                        {userProfile?.role === 'superadmin' && <Popconfirm
                                            title='Are you sure you want to delete this course from user?'
                                            onConfirm={() => handleRemoveCourse(item.id)}
                                            okText='Yes'
                                            cancelText='No'
                                        >
                                            <Button
                                                danger
                                                type='link'>
                                                Remove Course
                                            </Button>
                                        </Popconfirm>}
                                    </>
                                ]}
                                extra={
                                    <img
                                        width={272}
                                        alt="logo"
                                        src={item?.coverImage || DEFAULT_COURSE_COVER_IMAGE}
                                    />
                                }
                            >
                                <List.Item.Meta
                                    title={<h2>{item.name}</h2>}
                                    description={item.description}
                                />
                                {renderFinancialInfo(item)}
                            </List.Item>
                        )}
                    />
                </div>

                <AddUserCourse
                    isOpen={isAddUserCourseOpen}
                    setOpen={setIsAddUserCourseOpen}
                    handleGetUserCources={handleGetUserCources}
                    userId={userId}
                    userCources={cources}
                />
            </Spin>
        </div>
    )
}

export default UserCources